/** Mieterportal 2.0 (MP-B): Kontoauszug, Rückblick, Auszahlungsanfragen, Push-Abos. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { kontoauszug, monatsrueckblick, auszahlungsanfragen } from "../../worker/data/mieterkonto";

export type Kontoauszug = Awaited<ReturnType<typeof kontoauszug>>;
export type Rueckblick = Awaited<ReturnType<typeof monatsrueckblick>>;
export type Auszahlungsanfrage = Awaited<ReturnType<typeof auszahlungsanfragen>>[number];

export const useKonto = (von: string, bis: string) => useQuery({ queryKey: ["mieter", "konto", von, bis], queryFn: () => rufe<Kontoauszug>(api.api.mieter.konto.$get({ query: { von, bis } })) });
export const useRueckblick = (monat?: string) => useQuery({ queryKey: ["mieter", "rueckblick", monat ?? ""], queryFn: () => rufe<Rueckblick>(api.api.mieter.rueckblick.$get({ query: { monat } })) });
export const useAuszahlungAnfragen = () => useQuery({ queryKey: ["mieter", "auszahlung-anfragen"], queryFn: () => rufe<{ anfragen: Auszahlungsanfrage[] }>(api.api.mieter["auszahlung-anfragen"].$get()) });
export const usePushAbos = () => useQuery({ queryKey: ["mieter", "push-abos"], queryFn: () => rufe<{ abos: { endpoint: string; userAgent: string; erstelltAt: string }[] }>(api.api.mieter.push.abos.$get()) });
export const usePublicConfig = () => useQuery({ queryKey: ["public", "config"], queryFn: () => rufe<{ turnstileSiteKey: string | null; vapidPublicKey: string | null }>(api.api.public.config.$get()), staleTime: Infinity });

export function useAuszahlungAnfordern() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (betragCent: number) => rufe<{ id: string }>(api.api.mieter["auszahlung-anfordern"].$post({ json: { betragCent } })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["mieter"] }) });
}

export const kontoPdfUrl = (von: string, bis: string) => `/api/mieter/konto.pdf?von=${von}&bis=${bis}`;
export const verkaeufeCsvUrl = (von: string, bis: string) => `/api/mieter/export/verkaeufe.csv?von=${von}&bis=${bis}`;
export const jahresPdfUrl = (jahr: number) => `/api/mieter/jahresuebersicht/${jahr}`;

function urlBase64ToUint8Array(s: string): Uint8Array {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

/** Push einschalten: Erlaubnis, Abo beim Browser, Abo an den Server. */
export async function pushEinschalten(vapidPublicKey: string): Promise<"ok" | "verweigert" | "nicht_moeglich"> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return "nicht_moeglich";
  const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register("/sw.js"));
  const erlaubnis = await Notification.requestPermission();
  if (erlaubnis !== "granted") return "verweigert";
  const abo = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource });
  const j = abo.toJSON();
  await rufe(api.api.mieter.push.abo.$post({ json: { endpoint: abo.endpoint, keys: { p256dh: j.keys!.p256dh!, auth: j.keys!.auth! } } }));
  return "ok";
}

export async function pushAusschalten(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration();
  const abo = await reg?.pushManager.getSubscription();
  if (abo) {
    await rufe(api.api.mieter.push.abo.$delete({ json: { endpoint: abo.endpoint } }));
    await abo.unsubscribe();
  }
}

export async function pushAktiv(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  return !!(await reg?.pushManager.getSubscription());
}

/** E9: SEPA-Lastschriftmandat für die Miete, Mahnungen. */
export interface MandatInfo {
  mandat: { id: string; referenz: string; erteiltAt: string; ibanMaske: string } | null;
  glaeubigerId: string;
  moeglich: boolean;
  ibanMaske: string | null;
}
export const useMandat = () => useQuery({ queryKey: ["mieter", "mandat"], queryFn: () => rufe<MandatInfo>(api.api.mieter.mandat.$get()) });
export function useMandatAktion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { art: "erteilen" } | { art: "widerrufen"; id: string }) => (v.art === "erteilen" ? rufe<{ id: string; referenz: string }>(api.api.mieter.mandat.$post({ json: { bestaetigt: true } })) : rufe(api.api.mieter.mandat[":id"].$delete({ param: { id: v.id } }))),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["mieter"] }),
  });
}
export interface MieterMahnung {
  id: string;
  stufe: number;
  gebuehrCent: number;
  rechnungNummer: string;
  bruttoCent: number;
  rechnungStatus: string;
  faelligAt: string;
  erstelltAt: string;
  stornierAt: string | null;
}
export const useMieterMahnungen = () => useQuery({ queryKey: ["mieter", "mahnungen"], queryFn: () => rufe<{ mahnungen: MieterMahnung[] }>(api.api.mieter.mahnungen.$get()) });
